import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Breadcrumb, Button, Col, Container, Row } from 'react-bootstrap';
import { LuBookOpen, LuChevronDown, LuChevronUp, LuNewspaper, LuZap } from 'react-icons/lu';

import BlogPostCard from '../app/components/BlogPostCard';
import GradientCTA from '../app/components/GradientCTA';
import HeroAnimated from '../app/components/HeroAnimated';
import ResourcesHeroWaves from '../app/components/ResourcesHeroWaves';
import SbrIcon from '../app/components/SbrIcon';
import {
  fetchLatestSbrBlogPosts,
  type SbrBlogPostSummary,
} from '../lib/sbrBlog';

const INITIAL_VISIBLE = 6;

export default function BlogIndexPage() {
  const [posts, setPosts] = useState<SbrBlogPostSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const list = await fetchLatestSbrBlogPosts();
        if (!cancelled) setPosts(list);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const visiblePosts = expanded ? posts : posts.slice(0, INITIAL_VISIBLE);
  const hasMore = posts.length > INITIAL_VISIBLE;

  return (
    <>
      <section className="hero-pattern text-white resources-hero blog-index-hero">
        <div className="blog-index-hero__glow" aria-hidden />

        <Container className="resources-hero__container position-relative">
          <Breadcrumb className="solution-hero-breadcrumb mb-3">
            <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/resources#blog' }}>
              Resources
            </Breadcrumb.Item>
            <Breadcrumb.Item active>Blog</Breadcrumb.Item>
          </Breadcrumb>

          <Row className="resources-hero__row gy-3">
            <Col lg={7} className="d-flex flex-column">
              <HeroAnimated>
                <div data-hero-item className="blog-index-hero__pills mb-2">
                  <span className="blog-index-hero__pill">
                    <SbrIcon icon={LuNewspaper} size={14} className="me-1" />
                    {loading ? '…' : `${posts.length} articles`}
                  </span>
                  <span className="blog-index-hero__pill blog-index-hero__pill--alt">
                    <SbrIcon icon={LuZap} size={14} className="me-1" />
                    Hiring insights
                  </span>
                  <span className="blog-index-hero__pill blog-index-hero__pill--ghost">
                    <SbrIcon icon={LuBookOpen} size={14} className="me-1" />
                    Read on-site
                  </span>
                </div>

                <h1 data-hero-item className="display-5 fw-bold mb-2">
                  <span className="text-gradient-teal-blue">Blog</span>
                </h1>
                <p data-hero-item className="lead text-white-50 mb-0 pe-lg-3">
                  Hiring insights, talent strategy, and leadership perspectives from the SBR2TH team.
                </p>
              </HeroAnimated>
            </Col>
            <Col lg={5} className="resources-hero__media-col">
              <ResourcesHeroWaves />
            </Col>
          </Row>
        </Container>
      </section>

      <section className="resources-content bg-sbr-light py-5 py-lg-6 blog-index-grid">
        <div className="blog-index-grid__orb blog-index-grid__orb--left" aria-hidden />
        <div className="blog-index-grid__orb blog-index-grid__orb--right" aria-hidden />

        <Container className="position-relative">
          {!loading && posts.length > 0 ? (
            <p className="blog-index-grid__eyebrow">
              <span className="blog-index-grid__eyebrow-dot" aria-hidden />
              Latest from the team
            </p>
          ) : null}

          {loading ? (
            <p className="text-secondary mb-0">Loading blog posts…</p>
          ) : (
            <>
              <Row className="g-4 blog-index-grid__row">
                {visiblePosts.map((post, index) => (
                  <Col
                    key={post.id}
                    sm={6}
                    lg={4}
                    className={index % 3 === 1 ? 'blog-index-grid__col--nudge' : undefined}
                  >
                    <div className="blog-index-grid__card-wrap">
                      <span className="blog-index-grid__index" aria-hidden>
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <BlogPostCard post={post} />
                    </div>
                  </Col>
                ))}
              </Row>

              {hasMore ? (
                <div className="blog-index-expand text-center mt-5">
                  <Button
                    type="button"
                    variant="outline-primary"
                    className="blog-index-expand__btn"
                    onClick={() => setExpanded((open) => !open)}
                    aria-expanded={expanded}
                  >
                    <span>
                      {expanded
                        ? 'Show fewer posts'
                        : `Show all ${posts.length} posts`}
                    </span>
                    <SbrIcon
                      icon={expanded ? LuChevronUp : LuChevronDown}
                      size={20}
                      className="ms-2"
                    />
                  </Button>
                  {!expanded ? (
                    <p className="blog-index-expand__hint text-secondary small mb-0 mt-2">
                      Showing {INITIAL_VISIBLE} of {posts.length}
                    </p>
                  ) : null}
                </div>
              ) : null}
            </>
          )}
        </Container>
      </section>

      <GradientCTA
        icon={<i className="bi bi-calendar-event fs-4" aria-hidden />}
        title={
          <>
            <div>Want the playbook behind these resources?</div>
            <div className="sbr-cta__subtitle fw-normal opacity-75 mt-2">
              Schedule a working session with our team.
            </div>
          </>
        }
      />
    </>
  );
}
